import { useEffect, useState } from 'react';

/**
 * How wide the cart pane actually is, in pixels.
 *
 * The register's panes are sized by a seam the cashier drags, so a viewport
 * breakpoint says nothing useful about either of them any more: a 1920px screen
 * can hold a 300px cart, and `md:` would happily lay out six columns inside it.
 * Anything that has to react to a pane's width has to measure the pane.
 *
 * Read from the grid's own RESOLVED `grid-template-columns` rather than
 * reconstructed from the percentage — that string comes back as pixels
 * ("343px 14px 1029px"), so it already accounts for the divider, both gaps and
 * whatever the browser did with the fr rounding. Rebuilding it from `--pos-split`
 * would mean copying those constants here, where they would quietly rot the day
 * someone changes `gap-3`.
 *
 * Returns null before the first measurement and whenever the grid has collapsed
 * to a single column, which is the stacked layout below `lg` — there the cart is
 * full-width and nothing needs narrowing.
 */
export function useCartWidth(
  ref: React.RefObject<HTMLElement | null>,
  /** Re-measure when this changes: dragging the seam rewrites the template
   *  without resizing the container, so a ResizeObserver alone never fires. */
  revision: number,
): number | null {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const read = (): void => {
      const cols = getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean);
      // One column is the stacked layout, not a narrow cart.
      if (cols.length < 2) { setWidth(null); return; }
      const last = parseFloat(cols[cols.length - 1] ?? '');
      setWidth(Number.isFinite(last) ? last : null);
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, revision]);

  return width;
}

/**
 * Below this the cart drops its barcode column — the first thing to go, because
 * it is the only cell a cashier can recover by opening the line.
 */
export const CART_TIGHT_PX = 760;
/** Below this the controls take a line of their own under the product name. */
export const CART_STACKED_PX = 620;
